import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * ABONELİK SÖZLEŞMELERİ (M6a).
 *
 * ⚠⚠ HİÇBİR ÇAĞRI FATURA DÜZENLEMEZ. `generate` yalnız TASLAK üretir;
 * düzenleme geri dönüşsüzdür (numara yakar, yasal belge doğurur, Peppol'e
 * gider) ve `/invoices/:id/issue` üzerinden, insan onayıyla yapılır.
 */
export type SubscriptionPeriodicity = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
export type SubscriptionBillingMode = 'ARREARS' | 'ADVANCE';
export type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'ENDED';

export const PERIODICITY_LABELS: Record<SubscriptionPeriodicity, string> = {
  MONTHLY: 'Aylık',
  QUARTERLY: 'Üç aylık',
  YEARLY: 'Yıllık',
};

/**
 * ⚠ ETİKETLER KDV SONUCUNU DA SÖYLÜYOR: sürekli hizmet dönemin BİTİMİNDE ifa
 * edilmiş sayılır (art. 22 §2), ama önden kesilen faturada B2B müşteride KDV
 * FATURA TARİHİNDE muaccel olur (art. 22bis §1). Operatör bu farkı seçim
 * anında görmeli.
 */
export const BILLING_MODE_LABELS: Record<SubscriptionBillingMode, string> = {
  ARREARS: 'Sondan (dönem bitince)',
  ADVANCE: 'Önden (dönem başında — KDV erken muaccel olur)',
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Yürürlükte',
  PAUSED: 'Duraklatıldı',
  ENDED: 'Bitti',
};

export interface SubscriptionLine {
  id: string;
  lineNo: number;
  itemId: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
}

export interface Subscription {
  id: string;
  contactId: string;
  name: string;
  periodicity: SubscriptionPeriodicity;
  billingMode: SubscriptionBillingMode;
  startDate: string;
  endDate: string | null;
  nextPeriodStart: string;
  paymentTermDays: number;
  status: SubscriptionStatus;
  notes: string | null;
  lines: SubscriptionLine[];
  /**
   * Bir dönemin KDV HARİÇ tutarı — SUNUCU hesaplar.
   *
   * ⚠⚠ PANEL BUNU KENDİSİ HESAPLIYORDU ve sonuç ölçülebilir biçimde AYRIYDI:
   * `Math.round(Number(adet) * Number(fiyat) * 100)` ile `money.ts`in dört
   * haneli ölçekleyip yarım yukarı bölen yolu 1 kuruş ayrışıyor ve panel HER
   * ZAMAN düşük gösteriyordu (`1,02 × 37,25` → sunucu 38,00 / panel 37,99).
   * Yani operatörün gördüğü tutar, müşteriye gidecek faturadan farklıydı.
   *
   * ⚠ KDV İÇERMEZ: oran kalemin nitelemesinden ve müşterinin durumundan çıkar
   * (ters yükümlülükte sıfır) ve o karar düzenleme anında verilir.
   */
  periodAmountExclVat: string;
}

export interface DueSubscription {
  subscription: Subscription;
  period: { start: string; end: string };
  /** ⚠ KB n° 1 md. 4 §3 — dönemin bitimini izleyen ayın 15'i. */
  invoiceDeadline: string;
  /** ⚠ HİÇBİR YERE DONMAZ ve hiçbir şeyi ENGELLEMEZ — operatöre not. */
  warnings: string[];
}

export interface SubscriptionRun {
  id: string;
  periodStart: string;
  periodEnd: string;
  salesInvoiceId: string;
  createdAt: string;
}

export function useSubscriptions(status?: SubscriptionStatus) {
  return useQuery({
    queryKey: ['subscriptions', status ?? 'all'],
    queryFn: () => api.get<Subscription[]>(`/subscriptions${status ? `?status=${status}` : ''}`),
  });
}

/** ⚠ Bu uç hiçbir şey ÜRETMEZ — bir listedir. */
export function useDueSubscriptions() {
  return useQuery({
    queryKey: ['subscriptions', 'due'],
    queryFn: () => api.get<DueSubscription[]>('/subscriptions/due'),
  });
}

export function useSubscriptionRuns(id: string | null) {
  return useQuery({
    queryKey: ['subscription-runs', id],
    enabled: id !== null,
    queryFn: () => api.get<SubscriptionRun[]>(`/subscriptions/${String(id)}/runs`),
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      contactId: string;
      name: string;
      periodicity: SubscriptionPeriodicity;
      billingMode: SubscriptionBillingMode;
      startDate: string;
      endDate?: string | null;
      paymentTermDays?: number;
      lines: Array<{ itemId: string; quantity: string; unitPrice: string }>;
    }) => api.post<Subscription>('/subscriptions', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['subscriptions'] }),
  });
}

export function useSetSubscriptionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: SubscriptionStatus }) =>
      api.patch<Subscription>(`/subscriptions/${input.id}/status`, { status: input.status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['subscriptions'] }),
  });
}

/** ⚠ TASLAK üretir — düzenlemez. */
export function useGenerateSubscriptionInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ run: SubscriptionRun; warnings: string[] }>(`/subscriptions/${id}/generate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscriptions'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
